/**
 * Aesthetic painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const aestheticPaintingIdeasPack: ToilePack = {
    slug: 'aesthetic-painting-ideas',
    category: 'craft',
    name: 'Aesthetic painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'arch', label: 'Arch and sun', subject: 'a canvas of a terracotta arch framing a pale sun over soft dunes'},
        {key: 'vase', label: 'Dried flowers', subject: 'a canvas of a cream vase holding dried flowers and bunny-tail grass on a beige ground'},
        {key: 'window', label: 'Moon window', subject: 'a canvas of an open window with a sheer curtain lifting and a crescent moon outside'},
    ],
    styles: [
        {key: 'matte', label: 'Matte shapes', style: 'flat matte acrylic in soft rounded shapes with clean edges', palette: 'warm neutrals of terracotta, sand, cream and muted sage', framing: 'the canvas seen straight on, filling most of the frame, a sliver of plaster wall around it'},
        {key: 'textured', label: 'Textured', style: 'thick textured acrylic with raised palette-knife ridges and a sculpted relief', palette: 'tone-on-tone cream, beige and soft white with one terracotta accent', framing: 'the canvas leaning on a light oak shelf against a limewashed wall, a small ceramic vase beside it'},
        {key: 'washes', label: 'Soft washes', style: 'loose watered-down acrylic washes with soft bleeding edges and thin pencil details', palette: 'dusty pink, pale peach and warm grey', framing: 'the canvas hung above a bed with rumpled linen, seen at a slight angle'},
    ],
    directions: ['sage green', 'for a bedroom', 'a seashell'],
    directionHint: 'try: sage green',
    seo: {
        title: 'Aesthetic Painting Ideas: Arch, Flowers, Moon Window',
        metaDescription: 'Nine aesthetic canvas ideas in warm neutrals: a terracotta arch, dried flowers and an open window with a moon, in three soft finishes.',
        h1: 'Aesthetic painting ideas: terracotta arch, dried flowers, moon window',
        tagline: 'Nine finished canvases in warm neutrals, three subjects, each in a matte, a textured and a soft-wash finish.',
        intro: 'Aesthetic canvases are painted to match a room and a mood: warm neutrals, soft shapes and a calm, dreamy feel. These nine show three subjects: a terracotta arch framing a pale sun over soft dunes, a cream vase of dried flowers and bunny-tail grass, and an open window with a sheer curtain and a crescent moon outside. Type another subject, such as sage green or a seashell, and the same mood carries it.',
        useCases: [
            'a bedroom wall piece',
            'a calm corner above a bed',
            'a canvas beside a shelf of plants',
            'a housewarming gift',
        ],
        faq: [
            {q: 'What makes a canvas aesthetic?', a: 'Soft, warm colors and simple shapes that suit a room and set a calm mood. The three subjects follow that rule.'},
            {q: 'What are the three finishes?', a: 'Matte acrylic in smooth rounded shapes, a textured finish with raised palette-knife ridges, and soft watered-down washes.'},
            {q: 'Are these real artworks by an artist?', a: 'No. All nine are original images made to look like finished canvases.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the warm neutral palette and the three finishes carry it.'},
        ],
    },
}
